/* ============================================================
   Ketone engine · WebGL1 fragment shader
   One fullscreen triangle. A domain-warped noise field that is read
   as three states, driven by scroll progress (uP):
     0.0  slow, clustered, cold      -> stored fat
     0.5  stretched, moving, warm    -> ketones released
     1.0  fast, bright, converged    -> clean burn
   ============================================================ */
(function () {
  'use strict';

  const VERT = `
    attribute vec2 aPos;
    void main(){ gl_Position = vec4(aPos, 0.0, 1.0); }
  `;

  const FRAG = `
    precision highp float;
    uniform vec2  uRes;
    uniform float uTime;
    uniform float uP;
    uniform vec2  uPtr;

    float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); }

    float noise(vec2 p){
      vec2 i = floor(p), f = fract(p);
      f = f * f * (3.0 - 2.0 * f);
      return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x),
                 mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), f.x), f.y);
    }

    float fbm(vec2 p){
      float a = 0.5, s = 0.0;
      for (int i = 0; i < 5; i++) { s += a * noise(p); p *= 2.03; a *= 0.5; }
      return s;
    }

    void main(){
      vec2 uv = (gl_FragCoord.xy - 0.5 * uRes) / uRes.y;
      float p = clamp(uP, 0.0, 1.0);
      float ease = p * p * (3.0 - 2.0 * p);

      /* globules are isotropic and slow; ketone streams are stretched and quick */
      vec2  q = uv * mix(vec2(2.70, 2.70), vec2(0.62, 5.40), ease);
      float t = uTime * mix(0.045, 0.40, ease);

      vec2 w = vec2(fbm(q + vec2(0.0, t)), fbm(q + vec2(5.2, 1.3) - t));
      float f = fbm(q + 1.85 * w + vec2(t * 1.35, -t * 0.6));

      /* pointer adds a little local heat, so the field feels alive under the cursor */
      float ptr = exp(-length(uv - uPtr) * 3.2);
      f += ptr * 0.06;

      float body  = smoothstep(0.30, 0.585, f);
      float shell = smoothstep(0.355, 0.50, f) - smoothstep(0.50, 0.665, f);
      float core  = smoothstep(0.70, 1.00, f);

      vec3 deep = vec3(0.541, 0.118, 0.125);
      vec3 hot  = vec3(1.000, 0.267, 0.220);
      vec3 bone = vec3(0.949, 0.941, 0.925);

      vec3 col = vec3(0.018, 0.020, 0.023);
      col = mix(col, deep, body * (0.60 + 0.22 * ease));
      col = mix(col, hot,  pow(shell, 1.45) * (0.26 + 0.78 * ease));
      col += bone * pow(core, 2.3) * (0.05 + 0.62 * ease);
      col += hot * ptr * 0.10;

      /* a faint anisotropic sheen that only shows up once the streams are moving */
      float sheen = fbm(uv * vec2(0.5, 22.0) + vec2(0.0, t * 2.4));
      col += hot * pow(smoothstep(0.64, 1.0, sheen), 3.0) * ease * 0.16;

      col = pow(max(col, 0.0), vec3(0.94));
      col += (hash(gl_FragCoord.xy + fract(uTime)) - 0.5) * 0.012;   /* dither, kills banding */
      gl_FragColor = vec4(col, 1.0);
    }
  `;

  function compile(gl, type, src) {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      console.warn('[engine] shader:', gl.getShaderInfoLog(s));
      return null;
    }
    return s;
  }

  window.KorrectEngine = function (canvas) {
    const gl = canvas.getContext('webgl', { antialias: false, alpha: false, powerPreference: 'high-performance' })
            || canvas.getContext('experimental-webgl');
    if (!gl) return null;

    const vs = compile(gl, gl.VERTEX_SHADER, VERT);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return null;

    const prog = gl.createProgram();
    gl.attachShader(prog, vs); gl.attachShader(prog, fs); gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      console.warn('[engine] link:', gl.getProgramInfoLog(prog));
      return null;
    }
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(prog, 'aPos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    const uRes = gl.getUniformLocation(prog, 'uRes');
    const uTime = gl.getUniformLocation(prog, 'uTime');
    const uP = gl.getUniformLocation(prog, 'uP');
    const uPtr = gl.getUniformLocation(prog, 'uPtr');

    const state = { p: 0, pTarget: 0, ptr: [2, 2], ptrTarget: [2, 2], run: false, raf: 0, t0: performance.now() };

    function resize() {
      // a soft fluid field has no fine detail to lose, and 1.75x on a retina
      // display meant ~4 megapixels every frame for no visible gain
      // phones pay the most for this shader, and at this size the soft field
      // is indistinguishable at 1x
      const cap = window.innerWidth < 900 ? 1 : 1.35;
      const dpr = Math.min(window.devicePixelRatio || 1, cap);
      const w = Math.max(1, Math.round(canvas.clientWidth * dpr));
      const h = Math.max(1, Math.round(canvas.clientHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w; canvas.height = h;
        gl.viewport(0, 0, w, h);
      }
      gl.uniform2f(uRes, canvas.width, canvas.height);
    }

    function frame(now) {
      if (!state.run) return;
      state.raf = requestAnimationFrame(frame);
      resize();
      state.p += (state.pTarget - state.p) * 0.075;
      state.ptr[0] += (state.ptrTarget[0] - state.ptr[0]) * 0.06;
      state.ptr[1] += (state.ptrTarget[1] - state.ptr[1]) * 0.06;
      gl.uniform1f(uTime, (now - state.t0) / 1000);
      gl.uniform1f(uP, state.p);
      gl.uniform2f(uPtr, state.ptr[0], state.ptr[1]);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    return {
      start() { if (!state.run) { state.run = true; state.raf = requestAnimationFrame(frame); } },
      stop() { state.run = false; cancelAnimationFrame(state.raf); },
      setProgress(v) { state.pTarget = v; },
      setPointer(x, y) { state.ptrTarget[0] = x; state.ptrTarget[1] = y; },
      renderOnce() { state.run = true; resize(); state.p = state.pTarget;
                     gl.uniform1f(uTime, 0); gl.uniform1f(uP, state.p);
                     gl.uniform2f(uPtr, 2, 2); gl.drawArrays(gl.TRIANGLES, 0, 3); state.run = false; },
      destroy() { this.stop(); gl.deleteProgram(prog); gl.deleteBuffer(buf); }
    };
  };
})();
